// Paper doll compositor - composites layered PNG images onto a canvas

/**
 * @typedef {object} CompositeLayer
 * @property {string} imageUrl - URL to the layer image
 * @property {number} zOrder - Compositing order (lower = further back)
 */

// Draw order by category/subcategory. Compound keys (e.g. 'hair-addon:bottom')
// override the base category entry for specific sublayer groups or depths.
/** @type {Record<string, number>} */
const LAYER_Z_ORDER = {
  'gear-handheld:back': 0,
  tail: 1,
  legs: 2,
  'gear-legs:legs': 3,
  'gear-legs:kneepad': 4,
  'gear-footwear': 5,
  torso: 6,
  'gear-neck:back': 7,
  'gear-waist:bottom': 8,
  'gear-torso': 9,
  'gear-waist:waist': 10,
  'gear-neck:shoulder': 11,
  'gear-neck:neck': 12,
  'gear-arms:arms': 13,
  'gear-arms:hands': 14,
  'gear-arms:wrists': 15,
  'hair-addon:beard': 16,
  head: 17,
  ears: 18,
  'hair-addon:bottom': 19,
  hair: 20,
  'hair-addon:front': 21,
  'hair-addon:bangs': 22,
  'hair-addon:top': 23,
  horns: 24,
  tusks: 24,
  'gear-hat': 25,
  'gear-handheld:front': 26,
  'gear-handheld:combined': 26,
};

// Maps gear-neck subcategories to their z-order group
/** @type {Record<string, string>} */
const GEAR_NECK_GROUPS = {
  'cape-clean': 'back',
  'cape-ragged': 'back',
  pauldrons: 'shoulder',
  cowl: 'neck',
  'fur-mantel': 'neck',
  scarf: 'neck',
};

// Maps gear-waist subcategories to their z-order group
/** @type {Record<string, string>} */
const GEAR_WAIST_GROUPS = {
  'jerkin-bottom': 'bottom',
  belt: 'bottom',
  'front-pouch': 'bottom',
  'front-sash': 'bottom',
};

// Maps gear-legs subcategories to their z-order group
/** @type {Record<string, string>} */
const GEAR_LEGS_GROUPS = {
  pants: 'legs',
  'skirt-side': 'legs',
  'skirt-split': 'legs',
  kneepad: 'kneepad',
};

// Maps gear-arms subcategories to their z-order group
/** @type {Record<string, string>} */
const GEAR_ARMS_GROUPS = {
  'arm-armor': 'arms',
  'metal-bracers': 'arms',
  'fitted-gloves-long': 'hands',
  'fitted-gloves-medium': 'hands',
  'fitted-gloves-short': 'hands',
  'gloves-long': 'hands',
  bracelets: 'wrists',
};

/**
 * Compute draw order for a paperdoll layer from its category and attributes.
 * Looks up compound keys first (category:subcategory-prefix or category:depth),
 * then falls back to plain category.
 * @param {{ category: string, subcategory?: string, depth?: string }} layer
 * @returns {number}
 */
export function getLayerZOrder(layer) {
  if (layer.category === 'hair-addon') {
    const prefix = layer.subcategory?.split('-')[0];
    return LAYER_Z_ORDER[`hair-addon:${prefix}`] ?? LAYER_Z_ORDER.hair ?? 0;
  }
  if (layer.category === 'gear-handheld') {
    return LAYER_Z_ORDER[`gear-handheld:${layer.depth || 'combined'}`] ?? 0;
  }
  if (layer.category === 'gear-neck') {
    const group = GEAR_NECK_GROUPS[layer.subcategory ?? ''] || 'neck';
    return LAYER_Z_ORDER[`gear-neck:${group}`] ?? 0;
  }
  if (layer.category === 'gear-legs') {
    const group = GEAR_LEGS_GROUPS[layer.subcategory ?? ''] || 'legs';
    return LAYER_Z_ORDER[`gear-legs:${group}`] ?? 0;
  }
  if (layer.category === 'gear-waist') {
    const group = GEAR_WAIST_GROUPS[layer.subcategory ?? ''] || 'waist';
    return LAYER_Z_ORDER[`gear-waist:${group}`] ?? 0;
  }
  if (layer.category === 'gear-arms') {
    const group = GEAR_ARMS_GROUPS[layer.subcategory ?? ''] || 'arms';
    return LAYER_Z_ORDER[`gear-arms:${group}`] ?? 0;
  }
  return LAYER_Z_ORDER[layer.category] ?? 0;
}

/**
 * @typedef {object} SpeciesProfile
 * @property {number} scale - Uniform scale factor (1.0 = standard Medium)
 * @property {number} [scaleX] - Horizontal scale override (defaults to scale)
 * @property {number} translateY - Vertical pixel offset after scaling (in 1200x1200 space)
 */

/** Species visual scale profiles for compositing. */
export const SPECIES_PROFILES = {
  human: { scale: 1.0, translateY: 0 },
  halfling: { scale: 0.65, translateY: 80 },
  dwarf: { scale: 0.82, scaleX: 1.0, translateY: 35 },
  elf: { scale: 1.0, scaleX: 0.9, translateY: 0 },
  goliath: { scale: 1.18, translateY: -25 },
  orc: { scale: 1.0, scaleX: 1.1, translateY: 0 },
};

/**
 * Composite layers onto a canvas asynchronously.
 * Loads all images in parallel, sorts by zOrder, and draws back-to-front.
 * Optionally applies species scaling/translation.
 *
 * @param {HTMLCanvasElement} canvas - Target canvas element
 * @param {CompositeLayer[]} layers - Array of layers to composite
 * @param {SpeciesProfile} [species] - Optional species profile for scaling
 * @returns {Promise<void>}
 */
export async function compositeLayersAsync(canvas, layers, species) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  if (!layers || layers.length === 0) return;

  // Load all images in parallel
  const loaded = await Promise.all(
    layers.map((layer) =>
      loadImageAsync(layer.imageUrl).then((img) => ({
        img,
        zOrder: layer.zOrder,
      })),
    ),
  );

  // Sort by zOrder ascending (back to front)
  loaded.sort((a, b) => a.zOrder - b.zOrder);

  // Apply species transform if provided
  if (species) {
    const sx = species.scaleX ?? species.scale;
    const sy = species.scale;
    const cx = canvas.width / 2;
    const cy = canvas.height / 2;

    ctx.save();
    ctx.translate(cx, cy + species.translateY);
    ctx.scale(sx, sy);
    ctx.translate(-cx, -cy);
  }

  for (const { img } of loaded) {
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  }

  if (species) {
    ctx.restore();
  }
}

/**
 * Load an image from a URL.
 *
 * @param {string} url
 * @returns {Promise<HTMLImageElement>}
 */
function loadImageAsync(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
    img.src = url;
  });
}
